import { Box, Typography } from "@mui/material";
import { useMember } from "api/members";
import ProgressIndicator from "components/ProgressIndicator/ProgressIndicator";
import { createHeadItems, OLTable, Row } from "components/Table/OLTable";
import { useMessage } from "hooks/intl";
import { useNavigate, useParams } from "react-router-dom";
import { useTablesStateHelpers } from "state/tables/tableHelpers";
import { MEMBER_LEADERBOARD_LIST_ID } from "state/tables/types";
import { MemberLeaderboard } from "types/leaderboards/ranking";
import { links } from "utilities/links";
import { useStyles } from "../SingleMember.styles";

interface MemberLeaderboardTableProps {
  currentMemberLeaderboard: MemberLeaderboard;
  selectedLeaderboardId: string | false;
}

const MemberLeaderboardTable = ({
  currentMemberLeaderboard,
  selectedLeaderboardId,
}: MemberLeaderboardTableProps) => {
  const { message } = useMessage();
  const classes = useStyles();
  const navigate = useNavigate();
  const { id: memberID } = useParams<{ id: string }>();
  const { useMembersData } = useMember(memberID || "");
  const { data: currentMember } = useMembersData();
  const { setColumnOrdering } = useTablesStateHelpers();

  const headItems = createHeadItems([
    {
      headerText: message({ id: "position" }),
      property: "position",
      isSortable: true,
    },
    {
      headerText: message({ id: "fullName" }),
      property: "firstName",
      isSortable: true,
    },
    {
      headerText: message({ id: "email" }),
      property: "email",
      isSortable: true,
    },
    {
      headerText: message({ id: "loyaltyCardNumber" }),
      property: "loyaltyCardNumber",
    },
    {
      headerText: message({ id: "phoneNumber" }),
      property: "phoneNumber",
      isSortable: true,
    },
    {
      headerText: message({ id: "score" }),
      property: "score",
      isSortable: true,
      align: "right",
    },
  ]);

  const createItems = (): Row[] => {
    const items = [
      ...(currentMemberLeaderboard?.surrounding?.higher || []),
      {
        member: {
          customerId: currentMember?.customerId,
          firstName: currentMember?.firstName,
          lastName: currentMember?.lastName,
          email: currentMember?.email,
          phone: currentMember?.phone,
          loyaltyCardNumber: currentMember?.loyaltyCardNumber,
        },
        ranking: {
          score: currentMemberLeaderboard?.score,
          recalculatedAt: currentMemberLeaderboard?.recalculatedAt,
          position: currentMemberLeaderboard?.position,
          changedPositionAt: currentMemberLeaderboard?.changedPositionAt,
          progress: currentMemberLeaderboard?.progress,
        },
      },
      ...(currentMemberLeaderboard?.surrounding?.lower || []),
    ];

    return items.map((item) => ({
      cells: [
        {
          content: (
            <Box className={classes.positionContainer}>
              <Typography className={classes.position}>
                {item.ranking.position}
              </Typography>
              <ProgressIndicator progress={item.ranking.progress?.general} />
            </Box>
          ),
          property: "position",
        },
        {
          content: `${item.member.firstName || ""} ${item.member.lastName || ""}`,
          property: "firstName",
          dataHjSuppress: true,
        },
        {
          content: item.member.email,
          property: "email",
          dataHjSuppress: true,
        },
        {
          content: item.member.loyaltyCardNumber,
          property: "loyaltyCardNumber",
          dataHjSuppress: true,
        },
        {
          content: item.member.phone,
          property: "phoneNumber",
          dataHjSuppress: true,
        },
        {
          content: item.ranking.score,
          property: "score",
          dataHjSuppress: true,
          align: "right",
        },
      ],
      id: item.member.customerId as string,
    }));
  };

  const items = createItems();

  return (
    <OLTable
      columns={currentMemberLeaderboard && currentMember && headItems}
      rows={currentMemberLeaderboard && currentMember && items}
      tableName={MEMBER_LEADERBOARD_LIST_ID}
      actions={{
        body: [
          {
            action: (id) =>
              navigate(links.singleMemberGeneralInfo(id as string)),
            label: message({ id: "seeFullProfile" }),
            hideCondition: (item: Row) => item.id === currentMember?.customerId,
          },
          {
            action: () =>
              navigate(links.leaderboard(selectedLeaderboardId as string)),
            label: message({ id: "leaderboardDetails" }),
          },
        ],
      }}
      setColumnOrdering={setColumnOrdering}
      onSort={() => {}}
    />
  );
};

export default MemberLeaderboardTable;
